import { useState } from "react"
import { toast } from "react-toastify"
import {
  type ParseAdNameResult,
  type ParsedAdNameStatus,
  useAdForgeParseAdNameMutation,
} from "~/ui-rtk/api/adForgeApi"
import { Button } from "~/ui-rtk/components/ui/controls"
import Input from "~/ui-rtk/components/ui/controls/Input"
import { formatTagLabel } from "~/ui-rtk/types/structured-tags"

interface AdNameTesterProps {
  /** False until a convention has been saved — parsing needs one server-side. */
  hasSavedConvention: boolean
}

/** How each resolution outcome reads in the results table. */
const STATUS_STYLE: Record<
  ParsedAdNameStatus,
  { label: string; className: string }
> = {
  matched: { label: "Matched", className: "text-system-green-400" },
  created: { label: "Created", className: "text-brand" },
  would_create: { label: "New value", className: "text-system-yellow-400" },
  unknown_category: {
    label: "No such category",
    className: "text-system-red-400",
  },
}

/**
 * Paste a real ad name and see how the saved convention reads it: which token
 * lands in which tag category, and whether that value already exists.
 *
 * Always a DRY RUN — the request omits `create`, so checking a name can never
 * create taxonomy as a side effect of typing. It's the proving ground for the
 * convention before it's used to tag anything: if a name you already use comes
 * back with the tokens in the wrong categories, the positions are wrong.
 */
export default function AdNameTester({
  hasSavedConvention,
}: AdNameTesterProps) {
  const [adName, setAdName] = useState("")
  const [result, setResult] = useState<ParseAdNameResult | null>(null)
  const [parseAdName, { isLoading }] = useAdForgeParseAdNameMutation()

  const handleTest = async () => {
    const trimmed = adName.trim()
    if (!trimmed) return
    try {
      setResult(await parseAdName({ adName: trimmed }).unwrap())
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ??
        "Couldn't parse that ad name"
      toast.error(message)
      setResult(null)
    }
  }

  return (
    <div className="max-w-3xl space-y-2 border-t border-system-gray-800 pt-4">
      <div className="space-y-1">
        <span className="text-sm font-medium text-white">
          Test an existing ad name
        </span>
        <p className="text-xs text-system-gray-400">
          Check how the convention reads a name you already use. Nothing is
          saved — this only shows which tag each position would resolve to.
        </p>
      </div>

      <div className="flex items-start gap-2">
        <Input
          fullWidth
          value={adName}
          onChange={e => setAdName(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter") void handleTest()
          }}
          placeholder="Womens_Dave Video_Health Hack"
          disabled={!hasSavedConvention}
        />
        <Button
          variant={{ variant: "solid", color: "white" }}
          size="sm"
          type="button"
          isLoading={isLoading}
          onClick={() => void handleTest()}
          disabled={!hasSavedConvention || !adName.trim()}
        >
          Test
        </Button>
      </div>

      {!hasSavedConvention && (
        <p className="text-xs text-system-yellow-400">
          Save the convention above first — parsing uses the saved positions.
        </p>
      )}

      {result && (
        <div className="space-y-2">
          {result.tokens.length === 0 ? (
            <p className="text-xs text-system-gray-400">
              No positions matched that name.
            </p>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="text-system-gray-500">
                <tr>
                  <th className="py-1 pe-3 font-medium">#</th>
                  <th className="py-1 pe-3 font-medium">Category</th>
                  <th className="py-1 pe-3 font-medium">Token</th>
                  <th className="py-1 font-medium">Resolves to</th>
                </tr>
              </thead>
              <tbody>
                {result.tokens.map(token => {
                  const status = STATUS_STYLE[token.status]
                  return (
                    <tr
                      key={`${token.index}-${token.slug}`}
                      className="border-t border-system-gray-800"
                    >
                      <td className="py-1 pe-3 text-system-gray-500">
                        {token.index + 1}
                      </td>
                      <td className="py-1 pe-3 text-system-gray-200">
                        {formatTagLabel(token.categoryLabel ?? token.slug)}
                      </td>
                      <td className="py-1 pe-3 font-mono text-white">
                        {token.token}
                      </td>
                      <td className={`py-1 ${status.className}`}>
                        {status.label}
                        {token.optionLabel ? ` — ${token.optionLabel}` : ""}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}

          {result.missingSlugs.length > 0 && (
            <p className="text-xs text-system-yellow-400">
              This name has no value for: {result.missingSlugs.join(", ")}.
            </p>
          )}
          {result.extraTokens.length > 0 && (
            <p className="text-xs text-system-yellow-400">
              Extra segments beyond the last position:{" "}
              {result.extraTokens.join(", ")}. Add positions if these carry
              meaning.
            </p>
          )}
          {result.tokens.some(t => t.status === "would_create") && (
            <p className="text-xs text-system-gray-500">
              Values marked &quot;New value&quot; don&apos;t exist as tag
              options yet — they&apos;d be created when this name is actually
              tagged.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
